import { ApplicationServiceContainer } from '../container/DIContainer';
import { ProductDto, ProductSearchDto, CreateProductDto } from '../../application/dtos/ProductDto';
import { ProductId } from '../../domain/value-objects/ProductId';
import type { ProductDocument, ProductVariantDocument, ProductAggregateDocument } from '@/types/manage.types';
import { ProductFiltersSchema, type ProductFiltersDto } from '../../application/dtos/ProductQueryDto';

export class ProductService {
  private readonly serviceContainer = ApplicationServiceContainer.getSharedInstance();

  async getProducts(searchParams?: ProductSearchDto): Promise<ProductDto[]> {
    const getProductsUseCase = this.serviceContainer.resolveGetProductsUseCase();
    return await getProductsUseCase.execute(searchParams);
  }

  async getProductById(productId: string): Promise<ProductDto | null> {
    const productRepository = this.serviceContainer.resolveProductRepository();
    const productId_obj = ProductId.fromExistingId(productId);
    const product = await productRepository.findById(productId_obj);

    if (!product) {
      return null;
    }

    const variants = await productRepository.findVariantsByProductId(product.getId());

    return {
      id: product.getId().getUniqueIdentifier(),
      name: product.getName(),
      description: product.getDescription(),
      category: product.getCategory(),
      images: product.getImages(),
      slug: product.getSlug(),
      ingredients: product.getIngredients(),
      attributes: product.getAttributes(),
      allergenInfo: product.getAllergenInfo(),
      availability: product.getAvailability(),
      isVisible: product.isVisible(),
      createdAt: product.getCreatedAt().toISOString(),
      updatedAt: product.getUpdatedAt().toISOString(),
      variants: variants.map((variant) => ({
        id: variant.getId(),
        sku: variant.getSku(),
        productId: variant.getProductId().getUniqueIdentifier(),
        attributeCombination: variant.getAttributeCombination(),
        price: variant.getPrice().getAmountInDollars(),
        compareAtPrice: variant.getCompareAtPrice()?.getAmountInDollars(),
        availability: variant.getAvailability(),
        stockCount: variant.getStockCount(),
        images: variant.getImages(),
        weight: variant.getWeight(),
        dimensions: variant.getDimensions(),
        isDefault: variant.isDefault(),
        createdAt: variant.getCreatedAt().toISOString(),
        updatedAt: variant.getUpdatedAt().toISOString(),
        onStripe: variant.isOnStripe(),
        stripeInfo: variant.getStripeInfo(),
      })),
    };
  }

  async getProductBySlug(slug: string): Promise<ProductDto | null> {
    const productRepository = this.serviceContainer.resolveProductRepository();
    const product = await productRepository.findBySlug(slug);

    if (!product) {
      return null;
    }

    const variants = await productRepository.findVariantsByProductId(product.getId());

    return {
      id: product.getId().getUniqueIdentifier(),
      name: product.getName(),
      description: product.getDescription(),
      category: product.getCategory(),
      images: product.getImages(),
      slug: product.getSlug(),
      ingredients: product.getIngredients(),
      attributes: product.getAttributes(),
      allergenInfo: product.getAllergenInfo(),
      availability: product.getAvailability(),
      isVisible: product.isVisible(),
      createdAt: product.getCreatedAt().toISOString(),
      updatedAt: product.getUpdatedAt().toISOString(),
      variants: variants.map((variant) => ({
        id: variant.getId(),
        sku: variant.getSku(),
        productId: variant.getProductId().getUniqueIdentifier(),
        attributeCombination: variant.getAttributeCombination(),
        price: variant.getPrice().getAmountInDollars(),
        compareAtPrice: variant.getCompareAtPrice()?.getAmountInDollars(),
        availability: variant.getAvailability(),
        stockCount: variant.getStockCount(),
        images: variant.getImages(),
        weight: variant.getWeight(),
        dimensions: variant.getDimensions(),
        isDefault: variant.isDefault(),
        createdAt: variant.getCreatedAt().toISOString(),
        updatedAt: variant.getUpdatedAt().toISOString(),
        onStripe: variant.isOnStripe(),
        stripeInfo: variant.getStripeInfo(),
      })),
    };
  }

  async createProduct(productData: CreateProductDto): Promise<ProductDto> {
    const createProductUseCase = this.serviceContainer.resolveCreateProductUseCase();
    return await createProductUseCase.execute(productData);
  }

  async searchProducts(query: string): Promise<ProductDto[]> {
    return await this.getProducts({ query });
  }

  async getProductsByCategory(category: string): Promise<ProductDto[]> {
    return await this.getProducts({ category });
  }

  async getProductVariant(variantId: string) {
    const productRepository = this.serviceContainer.resolveProductRepository();
    const variant = await productRepository.findVariantById(variantId);

    if (!variant) {
      return null;
    }

    return {
      id: variant.getId(),
      sku: variant.getSku(),
      productId: variant.getProductId().getUniqueIdentifier(),
      attributeCombination: variant.getAttributeCombination(),
      price: variant.getPrice().getAmountInDollars(),
      compareAtPrice: variant.getCompareAtPrice()?.getAmountInDollars(),
      availability: variant.getAvailability(),
      stockCount: variant.getStockCount(),
      images: variant.getImages(),
      weight: variant.getWeight(),
      dimensions: variant.getDimensions(),
      isDefault: variant.isDefault(),
      createdAt: variant.getCreatedAt().toISOString(),
      updatedAt: variant.getUpdatedAt().toISOString(),
      onStripe: variant.isOnStripe(),
      stripeInfo: variant.getStripeInfo(),
    };
  }

  // Aggregation utilities for client hooks (menu/catalog)
  aggregateProducts(products: ProductDocument[], variants: ProductVariantDocument[]): ProductAggregateDocument[] {
    const variantsByProductId = variants.reduce(
      (acc, variant) => {
        (acc[variant.product_id] ||= []).push(variant);
        return acc;
      },
      {} as Record<string, ProductVariantDocument[]>,
    );

    return products
      .map((product) => {
        const productVariants = (variantsByProductId[product.id] || []).slice();
        // Sort variants by price ascending for stability
        productVariants.sort((a, b) => (a.price || 0) - (b.price || 0));
        return {
          ...product,
          variants: productVariants,
          created_at: product.created_at || Date.now(),
          updated_at: product.updated_at || Date.now(),
        } as ProductAggregateDocument;
      })
      .filter((product) => product.variants.length > 0);
  }

  filterAggregatedProducts(
    aggregated: ProductAggregateDocument[],
    filters?: ProductFiltersDto,
  ): ProductAggregateDocument[] {
    if (!filters) return aggregated;
    const parsed = ProductFiltersSchema.safeParse(filters);
    const f = parsed.success ? parsed.data : {};

    let res = aggregated.slice();

    if (f.category) {
      const cat = f.category.toLowerCase();
      res = res.filter((p) => p.category?.toLowerCase() === cat);
    }

    if (f.searchQuery) {
      const q = f.searchQuery.toLowerCase();
      res = res.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q),
      );
    }

    if (f.priceRange && (f.priceRange.max > 0 || f.priceRange.min > 0)) {
      const { min, max } = f.priceRange;
      res = res.filter((p) => {
        const prices = p.variants.map((v) => v.price || 0);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        return maxPrice >= min && minPrice <= (max || Number.MAX_SAFE_INTEGER);
      });
    }

    return res;
  }
}
